/** Aperçu A4 — même HTML/CSS que l’impression (`model-sheet.css`). */
import { formatSiret } from "../clients/format";
import { companyLetterheadUrl, companyLogoUrl } from "../../company/logoUrl";
import { formatCents } from "../../formats/display";
import { formatsFromCompany } from "../../formats/types";
import { footerLines, mergeCustom, sentencesToHtml, termSentences } from "../../legal/mentions";
import { opNaturePrint } from "../../legal/opNature";
import { modelBlockColor, modelBlockInk, type CompanyModel } from "../../model/types";
import { formatNumberPreview, numberingFromCompany } from "../../numbering/types";
import type { Company } from "../../types";

export type ModelDocKind = "invoice" | "quote" | "credit_note";

const TITLES: Record<ModelDocKind, string> = {
  invoice: "Facture",
  quote: "Devis",
  credit_note: "Avoir",
};

const CLIENT_K: Record<ModelDocKind, string> = {
  invoice: "Facturé à",
  quote: "Destinataire",
  credit_note: "Avoir au profit de",
};

type ModelPreviewProps = {
  company: Company;
  draft: CompanyModel;
  kind: ModelDocKind;
};

function customFor(company: Company, kind: ModelDocKind): { terms: string; footer: string } {
  if (kind === "quote") {
    return { terms: company.quote_terms || "", footer: company.quote_footer || "" };
  }
  if (kind === "credit_note") {
    return { terms: company.credit_terms || "", footer: company.credit_footer || "" };
  }
  return { terms: company.invoice_terms || "", footer: company.invoice_footer || "" };
}

export function ModelPreview({ company, draft, kind }: ModelPreviewProps) {
  const w = Math.round(240 * (draft.logo_pct / 100));
  const block = modelBlockColor(draft);
  const logo = company.letterhead_logo_path || company.logo_path;
  const city = [company.postal_code, company.city].filter(Boolean).join(" ");
  const ship = draft.ship_address && kind === "quote";
  const fmt = formatsFromCompany(company);
  const ttc = formatCents(120000, fmt);
  const siret = company.siret ? formatSiret(company.siret) : "";
  const due = kind === "quote" ? "Valable jusqu’au 16/10/2026" : "Échéance 16/10/2026";
  const number = formatNumberPreview(numberingFromCompany(company)[kind], "2026-09-16");
  const custom = customFor(company, kind);
  const duePhrase = kind === "quote" ? "16/10/2026" : "16/10/2026";
  const terms = mergeCustom(sentencesToHtml(termSentences(company, kind, duePhrase)), custom.terms);
  const footer = mergeCustom(sentencesToHtml(footerLines(company, siret)), custom.footer);

  return (
    <div
      className={`fx-mdl-sheet is-${draft.skin}`}
      style={{
        ["--mdl-p" as string]: draft.color_primary,
        ["--mdl-s" as string]: draft.color_secondary,
        ["--mdl-b" as string]: block,
        ["--mdl-b-ink" as string]: modelBlockInk(block),
        fontSize: `${draft.font_size}px`,
      }}
    >
      <header className="fx-mdl-top">
        <div className="fx-mdl-logo" style={{ width: w, ["--mdl-logo" as string]: `${w}px` }}>
          {logo ? (
            <img
              src={company.letterhead_logo_path ? companyLetterheadUrl(company) : companyLogoUrl(company)}
              alt=""
            />
          ) : (
            <span>Logo</span>
          )}
        </div>
        <section className="fx-mdl-doc" aria-label="Pièce">
          <b>{TITLES[kind]}</b>
          <div className="fx-mdl-facts">
            <span>N° {number}</span>
            {kind === "credit_note" ? <span>Facture F-2026-0001</span> : null}
            <span>Date d’émission 16/09/2026</span>
            {kind !== "quote" ? <span>Date d’opération 10/09/2026</span> : null}
            <span>{due}</span>
            <span>{opNaturePrint("SERVICES")}</span>
          </div>
          {draft.paid_stamp && kind === "invoice" ? <div className="fx-mdl-paid">Payé</div> : null}
          <div className="fx-mdl-ttc">
            <small>Total TTC</small>
            <strong>{ttc}</strong>
          </div>
        </section>
        <section className="fx-mdl-dest fx-mdl-issuer" aria-label="Émetteur">
          <small>Émis par</small>
          <strong>{company.legal_name || "Entreprise"}</strong>
          {company.address_line ? <em>{company.address_line}</em> : null}
          {company.address_line2 ? <em>{company.address_line2}</em> : null}
          {city ? <em>{city}</em> : null}
          {siret ? <em>SIRET {siret}</em> : null}
          {company.vat_number ? <em>TVA {company.vat_number}</em> : null}
          {draft.show_issuer_phone && company.phone ? <em>{company.phone}</em> : null}
          {draft.show_issuer_email && company.email ? <em>{company.email}</em> : null}
          {draft.show_issuer_iban && company.iban ? <em>IBAN {company.iban}</em> : null}
          {draft.show_issuer_iban && company.bic ? <em>BIC {company.bic}</em> : null}
        </section>
        <div className="fx-mdl-client">
          <section className="fx-mdl-dest" aria-label="Destinataire">
            <small>{CLIENT_K[kind]}</small>
            <strong>Client exemple</strong>
            <em>12 rue des Lilas</em>
            <em>Bâtiment B</em>
            <em>75011 Paris</em>
            <em>SIREN 123 456 789</em>
            <em>SIRET 123 456 789 00012</em>
            <em>TVA FR32 123456789</em>
            {draft.show_client_phone ? <em>01 23 45 67 89</em> : null}
            {draft.show_client_email ? <em>contact@client-exemple.fr</em> : null}
            {draft.show_client_contacts ? <em>Marie Dupont · marie@client-exemple.fr</em> : null}
          </section>
          {ship ? (
            <section className="fx-mdl-dest">
              <small>Livré à</small>
              <strong>Même adresse</strong>
              <em>12 rue des Lilas</em>
              <em>75011 Paris</em>
            </section>
          ) : null}
        </div>
      </header>

      <table className="fx-mdl-lines">
        <thead>
          <tr>
            <th>Désignation</th>
            <th>Prix unitaire</th>
            <th>Qté</th>
            <th>TVA</th>
            <th>Remise</th>
            <th>Total HT</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Prestation exemple</td>
            <td>1 000,00 €</td>
            <td>1</td>
            <td>20 %</td>
            <td></td>
            <td>1 000,00 €</td>
          </tr>
          {kind !== "quote" ? (
            <tr>
              <td>Fourniture exemple</td>
              <td>200,00 €</td>
              <td>1</td>
              <td>10 %</td>
              <td></td>
              <td>200,00 €</td>
            </tr>
          ) : null}
        </tbody>
      </table>

      <div className="fx-mdl-foot">
        <div className="fx-mdl-legal">
          {terms ? <div className="fx-mdl-terms" dangerouslySetInnerHTML={{ __html: terms }} /> : (
            <p className="fx-mdl-terms is-empty">Aucune condition renseignée pour cette pièce.</p>
          )}
        </div>
        <div className="fx-mdl-tot">
          <div><span>Total HT</span><b>{kind === "quote" ? "1 000,00 €" : "1 200,00 €"}</b></div>
          {kind === "quote" ? (
            <div><span>TVA 20 %</span><b>200,00 €</b></div>
          ) : (
            <>
              <div><span>TVA 10 % · base 200,00 €</span><b>20,00 €</b></div>
              <div><span>TVA 20 % · base 1 000,00 €</span><b>200,00 €</b></div>
            </>
          )}
          <div className="is-ttc"><span>Total TTC</span><b>{kind === "quote" ? ttc : "1 420,00 €"}</b></div>
        </div>
      </div>

      <footer className="fx-mdl-bottom">
        {footer ? <div className="fx-mdl-terms" dangerouslySetInnerHTML={{ __html: footer }} /> : (
          <p className="fx-mdl-terms is-empty">Aucun pied de page renseigné.</p>
        )}
        {draft.page_numbers ? (
          <div className={`fx-mdl-pg is-${draft.page_align}`}>1 / 1</div>
        ) : null}
        {draft.brand_mark ? (
          <p className="fx-mdl-brand">
            Édité avec{" "}
            <a href="https://aureliasuite.fr" target="_blank" rel="noopener noreferrer">
              Aurélia Factures
            </a>
          </p>
        ) : null}
      </footer>
    </div>
  );
}
